import * as THREE from "three";
import { mergeGeometries } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import RAPIER from "@dimforge/rapier3d-compat";
import { OBJECT_CATALOG, canFitInHole, objectById, objectByKind, type ObjectDefinition } from "./catalog";
import { audio } from "../audio";
import type { ItemState, PlayerState } from "./protocol";
import {
  START_RADIUS, MAX_RADIUS, RESPAWN_DELAY, applyKill, createBots, duel, leaderboard, moveBot, planBot,
  type Bot, type BotItem, type Contestant, type LeaderEntry,
} from "./bots";
import {
  BLOCK_CENTERS, BLOCK_HALF, MIN_ITEM_GAP, ROAD_HALF, ROAD_LINES, WORLD_HALF,
  advanceLane, blockSpot, clearOf, isInBlockInterior, laneSpot, layoutYaw, planCity, yawFor,
} from "./world";
import { botSkin, skinById } from "./skins";
import { ABYSS_DEPTH, HoleVisual } from "./holeVisual";
import { Effects, Shape } from "./particles";
import { Crowd, Part, applyDepthFade } from "./crowd";
import { shadowExtent, withinReach } from "./culling";
import { BlobShadows, PropBatch } from "./props";

/** Models that walk, fly or roll: rendered instanced with vertex animation. */
const ANIMATED_MODELS = new Set(["person", "dog", "cat", "bird", "bike", "taxi"]);
/** Gait radians per world unit travelled (2π / stride length). */
const STRIDE: Record<string, number> = { person: 5.2, dog: 7.5, cat: 8.5, bike: 3.0, taxi: 1, bird: 0 };
const SHIRTS = [0xe8505b, 0x3d8bfd, 0xf5b83d, 0x46c37b, 0x9b6bff, 0xff8c42, 0xf2f2f2, 0x2ec4b6, 0xff6fb5, 0x6c7a89];
import { detailTexture, type DetailPattern } from "./textures";

/** Player + up to 8 rivals can cut the ground open at the same time. */
const MAX_HOLES = 9;
/** Objects count as swallowed once their top is this far below ground. */
// The shaft is black below ~9 units and objects fade out on the way, so at
// 14 units they are gone from the screen for good.
const SWALLOW_DEPTH = Math.min(ABYSS_DEPTH, 14);

export interface GameStats {
  score: number;
  eaten: number;
  radius: number;
  time: number;
  combo: number;
  leaders?: LeaderEntry[];
  rank?: number;
  /** Seconds until respawn while the player hole is swallowed. */
  respawnIn?: number;
  /** Latest swallow event, e.g. "Gulpy swallowed you". */
  feed?: { text: string; id: number };
}

export interface GameResult {
  score: number;
  eaten: number;
  rank?: number;
  total?: number;
  kills?: number;
}

export interface GameEngineOptions {
  duration?: number;
  skinColor?: string;
  skinId?: string;
  onStats?: (stats: GameStats) => void;
  onFinish?: (result: GameResult) => void;
  authoritative?: boolean;
  onTarget?: (x: number, z: number) => void;
  /** Offline AI opponents (solo mode only). */
  bots?: number;
  playerName?: string;
  /** Localized feed templates: {a} swallowed {b}. */
  feedText?: { kill: string; killedBy: string; you: string };
}

interface WorldItem {
  id: number;
  definition: ObjectDefinition;
  root: THREE.Group;
  shadow: THREE.Object3D;
  x: number;
  z: number;
  /** Target position for authoritative snapshots (interpolated). */
  tx: number;
  tz: number;
  /** Velocity for living/moving kinds (traffic, pedestrians, birds). */
  vx: number;
  vz: number;
  falling: boolean;
  consumed: boolean;
  body?: RAPIER.RigidBody;
  respawnAt?: number;
  /** Planned plot position: static props always come back to their spot. */
  home?: { x: number; z: number; definition: ObjectDefinition };
}

interface SinkingItem {
  item: WorldItem;
  bot: Bot;
  t: number;
}

interface BotView {
  visual: HoleVisual;
  label: THREE.Sprite;
}

interface ScorePopup {
  sprite: THREE.Sprite;
  canvas: HTMLCanvasElement;
  texture: THREE.CanvasTexture;
  life: number;
  vy: number;
}

const WORLD_SIZE = 170;
const FLOOR_Y = 0;
const MAX_ITEMS = 340;
const RAIL = WORLD_HALF;

interface BoxSpec {
  w: number;
  h: number;
  d: number;
  x: number;
  y: number;
  z: number;
}

class SeededRandom {
  constructor(private value = 0x51f15e) {}
  next() {
    this.value |= 0;
    this.value = (this.value + 0x6d2b79f5) | 0;
    let t = this.value;
    t = Math.imul(t ^ (t >>> 15), 1 | t);
    t ^= t + Math.imul(t ^ (t >>> 7), 61 | t);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  range(min: number, max: number) { return min + (max - min) * this.next(); }
  pick<T>(list: readonly T[]): T { return list[Math.floor(this.next() * list.length)]!; }
}

export class GameEngine {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(48, 1, 0.1, 220);
  private readonly renderer: THREE.WebGLRenderer;
  private readonly clock = new THREE.Clock();
  private readonly items: WorldItem[] = [];
  private readonly fx = new Effects();
  private readonly popups: ScorePopup[] = [];
  private popupCursor = 0;
  private playerVisual?: HoleVisual;
  private readonly remoteVisuals = new Map<number, HoleVisual>();
  private sun?: THREE.DirectionalLight;
  private readonly random = new SeededRandom();
  private readonly keyState = new Set<string>();
  private readonly input = new THREE.Vector2();
  private readonly pointerAnchor = new THREE.Vector2();
  private readonly pointerCurrent = new THREE.Vector2();
  /** xyz = (x, z, radius) per open hole; radius 0 = unused slot. */
  private readonly holeUniforms = {
    holes: { value: Array.from({ length: MAX_HOLES }, () => new THREE.Vector3(0, 0, 0)) },
  };
  private readonly options: Required<Pick<GameEngineOptions, "duration">> & GameEngineOptions;
  private readonly holeGroup = new THREE.Group();
  private readonly remoteHoleGroup = new THREE.Group();
  private readonly cityGroup = new THREE.Group();
  private readonly effectsGroup = new THREE.Group();
  /** One shared merged geometry per kind: keeps draw calls and memory low. */
  private readonly modelGeometries = new Map<number, THREE.BufferGeometry>();
  private readonly propMaterial = (() => {
    const material = new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.78, metalness: 0.02 });
    // Falling objects fade into the dark shaft instead of staying lit.
    applyDepthFade(material);
    return material;
  })();
  private readonly crowd = new Crowd();
  /** Non-animated props: one instanced draw call per kind. */
  private readonly props = new PropBatch();
  /** Soft discs under large props, one instanced draw call total. */
  private readonly blobs = new BlobShadows();
  /** Blob anchors stay out of the scene graph so they are not drawn twice. */
  private readonly blobAnchors = new THREE.Group();
  private readonly coarse = typeof matchMedia === "function" && (matchMedia("(pointer: coarse)").matches || Math.min(window.innerWidth, window.innerHeight) < 800);
  private readonly _frustum = new THREE.Frustum();
  private readonly _proj = new THREE.Matrix4();
  private readonly _sphere = new THREE.Sphere();
  private readonly _camTarget = new THREE.Vector3();
  private readonly _camLook = new THREE.Vector3();
  private physics?: RAPIER.World;
  private rimBody?: RAPIER.RigidBody;
  private rimRadius = 0;
  private frame = 0;
  private started = false;
  private destroyed = false;
  private finished = false;
  private pointerActive = false;
  private elapsed = 0;
  private score = 0;
  private eaten = 0;
  private combo = 0;
  private comboLife = 0;
  private shake = 0;
  private statsAccumulator = 0;
  private targetAccumulator = 0;
  private authoritativePlayerId = 0;
  private readonly hole = { x: 0, z: 0, radius: 2.4, targetRadius: 2.4 };
  private resizeObserver?: ResizeObserver;
  private bots: Bot[] = [];
  private readonly botViews = new Map<number, BotView>();
  private readonly sinking: SinkingItem[] = [];
  private nextItemId = 100_000;
  private alive = true;
  private respawnAt = 0;
  private kills = 0;
  private feed?: { text: string; id: number };
  private feedId = 0;
  private arenaLeaders?: { leaders: LeaderEntry[]; rank: number };

  constructor(private readonly canvas: HTMLCanvasElement, options: GameEngineOptions = {}) {
    this.options = { duration: options.duration ?? 75, ...options };
    const sharp = !this.coarse && (window.devicePixelRatio || 1) < 2;
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: sharp,
      alpha: false,
      powerPreference: "high-performance",
      stencil: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = this.coarse ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
    this.scene.background = new THREE.Color(0x9fd8f2);
    this.scene.fog = new THREE.FogExp2(0x9fd8f2, 0.0085);
  }

  async start() {
    if (this.started) return;
    this.started = true;
    await RAPIER.init();
    if (this.destroyed) return;
    this.physics = new RAPIER.World({ x: 0, y: -24, z: 0 });
    this.configureRenderer();
    this.buildLights();
    this.buildCity();
    this.buildHole();
    this.buildPhysicsRim(true);
    if (!this.options.authoritative && (this.options.bots ?? 0) > 0) this.spawnBots(this.options.bots!);
    this.bindInput();
    this.clock.start();
    void audio.playMusic("play");
    void audio.playSfx("whoosh", 0.35);
    this.renderer.setAnimationLoop(this.animate);
  }

  syncArenaSnapshot(players: PlayerState[], items: ItemState[], playerId: number) {
    if (!this.options.authoritative) return;
    this.authoritativePlayerId = playerId;
    const local = players.find((player) => player.id === playerId);
    if (local) {
      this.hole.x = local.x;
      this.hole.z = local.z;
      this.hole.radius = Math.max(0.25, local.size);
      this.hole.targetRadius = this.hole.radius;
      this.score = local.score;
    }

    const visibleItems = new Set(items.map((item) => item.id));
    const worldById = new Map(this.items.map((item) => [item.id, item]));
    for (const state of items) {
      const definition = objectByKind(state.kind);
      let item = worldById.get(state.id);
      if (!item) {
        const root = this.createModel(definition);
        if (definition.motion === "none") root.rotation.y = layoutYaw(definition.id, state.x, state.z);
        const shadow = this.attachBlob(definition, state.x, state.z);
        item = {
          id: state.id, definition, root, shadow,
          x: state.x, z: state.z, tx: state.x, tz: state.z, vx: 0, vz: 0,
          falling: false, consumed: false,
        };
        this.items.push(item);
        worldById.set(state.id, item);
        root.matrixAutoUpdate = false;
        this.cityGroup.add(root);
      } else if (!item.falling && item.definition.kind !== definition.kind) {
        this.cityGroup.remove(item.root);
        item.root = this.createModel(definition);
        if (definition.motion === "none") item.root.rotation.y = layoutYaw(definition.id, state.x, state.z);
        item.definition = definition;
        this.cityGroup.add(item.root);
      }
      if (item.falling) continue;
      // The server owns all item positions in arena mode: store them as the
      // interpolation target and let updateItems() glide towards them.
      item.consumed = false;
      item.tx = state.x;
      item.tz = state.z;
      if (item.x === 0 && item.z === 0) { item.x = state.x; item.z = state.z; }
      item.root.position.set(item.x, definition.height / 2 + 0.18, item.z);
      item.shadow.position.set(item.x, 0.2, item.z);
      item.root.updateMatrix();
    }
    // Drop items the server no longer knows about so a long match cannot
    // accumulate thousands of hidden meshes.
    for (let index = this.items.length - 1; index >= 0; index--) {
      const item = this.items[index]!;
      if (!item.falling && !visibleItems.has(item.id)) {
        this.cityGroup.remove(item.root);
        this.blobAnchors.remove(item.shadow);
        this.items.splice(index, 1);
      }
    }

    const ranked = leaderboard(players.map((player) => ({
      name: player.id === playerId ? (this.options.playerName ?? "You") : `Hole ${player.id}`,
      score: player.score,
      size: player.size * 2,
      me: player.id === playerId,
    })));
    this.arenaLeaders = { leaders: ranked.slice(0, 5), rank: ranked.findIndex((entry) => entry.me) + 1 };

    const remoteIds = new Set<number>();
    for (const player of players) {
      if (player.id === playerId) continue;
      remoteIds.add(player.id);
      let visual = this.remoteVisuals.get(player.id);
      if (!visual) {
        visual = new HoleVisual(botSkin(player.id, this.options.skinId), { effects: this.fx, particleScale: 0.4 });
        this.remoteVisuals.set(player.id, visual);
        this.remoteHoleGroup.add(visual.group);
      }
      visual.targetX = player.x;
      visual.targetZ = player.z;
      visual.targetRadius = Math.max(0.25, player.size);
    }
    for (const [id, visual] of this.remoteVisuals) {
      if (!remoteIds.has(id)) {
        visual.dispose();
        this.remoteVisuals.delete(id);
      }
    }
  }

  onArenaEat(playerId: number, itemId: number) {
    if (!this.options.authoritative) return;
    const item = this.items.find((candidate) => candidate.id === itemId);
    if (item) this.activateItem(item);
    if (playerId === this.authoritativePlayerId) this.eaten++;
  }

  get eatenCount() { return this.eaten; }

  destroy() {
    this.destroyed = true;
    this.resizeObserver?.disconnect();
    this.unbindInput();
    this.renderer.setAnimationLoop(null);
    this.playerVisual?.dispose();
    for (const view of this.botViews.values()) view.visual.dispose();
    for (const visual of this.remoteVisuals.values()) visual.dispose();
    this.fx.dispose();
    this.crowd.dispose();
    this.props.dispose();
    this.blobs.dispose();
    for (const popup of this.popups) { popup.texture.dispose(); popup.sprite.material.dispose(); }
    this.scene.traverse((object) => {
      if (object instanceof THREE.Mesh) {
        object.geometry.dispose();
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        materials.forEach((material) => material.dispose());
      }
    });
    this.renderer.dispose();
  }

  private configureRenderer() {
    const resize = () => {
      const width = Math.max(1, this.canvas.clientWidth);
      const height = Math.max(1, this.canvas.clientHeight);
      const mobile = Math.min(width, height) < 700;
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, mobile ? 1.25 : 1.5));
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.fov = height > width ? 48 : 43;
      this.camera.updateProjectionMatrix();
      this.fx.setViewportHeight(height * this.renderer.getPixelRatio(), this.camera.fov);
    };
    this.resizeObserver = new ResizeObserver(resize);
    this.resizeObserver.observe(this.canvas);
    resize();
  }

  private buildLights() {
    const hemisphere = new THREE.HemisphereLight(0xe4f4ff, 0x6a7a55, 2.1);
    this.scene.add(hemisphere);

    const sun = new THREE.DirectionalLight(0xfff1cf, 4.1);
    sun.position.set(-38, 62, 28);
    sun.castShadow = true;
    const shadowSize = this.coarse ? 1024 : 1536;
    sun.shadow.mapSize.set(shadowSize, shadowSize);
    const extent = shadowExtent(30);
    sun.shadow.camera.left = -extent;
    sun.shadow.camera.right = extent;
    sun.shadow.camera.top = extent;
    sun.shadow.camera.bottom = -extent;
    sun.shadow.camera.near = 5;
    sun.shadow.camera.far = 130;
    sun.shadow.bias = -0.0005;
    sun.shadow.normalBias = 0.02;
    this.scene.add(sun);
    this.scene.add(sun.target);
    this.sun = sun;
  }

  private holeAwareMaterial(color: number, roughness = 0.86, detail?: DetailPattern, detailScale = 0.25): THREE.MeshStandardMaterial {
    const material = new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.02 });
    const detailMap = detail ? detailTexture(detail) : undefined;
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uHoles = this.holeUniforms.holes;
      shader.uniforms.uDetail = { value: detailMap };
      shader.uniforms.uDetailScale = { value: detailScale };
      shader.vertexShader = `varying vec3 vHoleWorld;
${shader.vertexShader}`.replace(
        "#include <worldpos_vertex>",
        `#include <worldpos_vertex>
vHoleWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;`,
      );
      shader.fragmentShader = `uniform vec3 uHoles[${MAX_HOLES}];
${detailMap ? "uniform sampler2D uDetail;\nuniform float uDetailScale;" : ""}
varying vec3 vHoleWorld;
${shader.fragmentShader}`.replace(
        "#include <clipping_planes_fragment>",
        `#include <clipping_planes_fragment>
for (int i = 0; i < ${MAX_HOLES}; i++) {
  vec3 h = uHoles[i];
  if (h.z > 0.0 && distance(vHoleWorld.xz, h.xy) < h.z) discard;
}`,
      ).replace(
        "#include <color_fragment>",
        detailMap
          ? `#include <color_fragment>
diffuseColor.rgb *= 0.72 + 0.56 * texture2D(uDetail, vHoleWorld.xz * uDetailScale).r;`
          : "#include <color_fragment>",
      );
    };
    material.customProgramCacheKey = () => `dunkles-loch-aperture-v2-${detail ?? "plain"}`;
    return material;
  }

  private buildCity() {
    this.scene.add(this.cityGroup, this.effectsGroup, this.fx.group, this.crowd.group, this.props.group, this.blobs.mesh);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(WORLD_SIZE, WORLD_SIZE),
      this.holeAwareMaterial(0x7cb262, 0.95, "grass", 0.18),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    ground.matrixAutoUpdate = false;
    ground.updateMatrix();
    this.cityGroup.add(ground);

    const sidewalkMaterial = this.holeAwareMaterial(0xd7d0c0, 0.95, "tiles", 0.5);
    const roadMaterial = this.holeAwareMaterial(0x4a5361, 0.98, "asphalt", 0.22);
    const laneMaterial = this.holeAwareMaterial(0xf3e2a4, 0.9);
    const crossMaterial = this.holeAwareMaterial(0xeceade, 0.9);

    // --- asphalt: one slab per road line, both directions ---
    this.addMergedBoxes(
      ROAD_LINES.flatMap((line) => [
        { w: WORLD_SIZE, h: 0.08, d: ROAD_HALF * 2, x: 0, y: 0.04, z: line },
        { w: ROAD_HALF * 2, h: 0.08, d: WORLD_SIZE, x: line, y: 0.04, z: 0 },
      ]),
      roadMaterial,
    );

    // --- dashed lane markings (skipped inside intersections) ---
    const dashes: BoxSpec[] = [];
    for (const line of ROAD_LINES) {
      for (let along = -WORLD_HALF + 3; along <= WORLD_HALF - 3; along += 7) {
        const nearCrossing = ROAD_LINES.some((cross) => Math.abs(along - cross) < ROAD_HALF + 2.5);
        if (nearCrossing) continue;
        dashes.push({ w: 0.22, h: 0.03, d: 3, x: line, y: 0.09, z: along });
        dashes.push({ w: 3, h: 0.03, d: 0.22, x: along, y: 0.09, z: line });
      }
    }
    this.addMergedBoxes(dashes, laneMaterial);

    // --- zebra crossings on every approach to an intersection ---
    const stripes: BoxSpec[] = [];
    for (const rx of ROAD_LINES) {
      for (const rz of ROAD_LINES) {
        for (const side of [-1, 1]) {
          const bandZ = rz + side * (ROAD_HALF + 1.4);
          const bandX = rx + side * (ROAD_HALF + 1.4);
          for (let offset = -ROAD_HALF + 0.6; offset <= ROAD_HALF - 0.6; offset += 1.15) {
            stripes.push({ w: 0.55, h: 0.025, d: 2.2, x: rx + offset, y: 0.095, z: bandZ });
            stripes.push({ w: 2.2, h: 0.025, d: 0.55, x: bandX, y: 0.095, z: rz + offset });
          }
        }
      }
    }
    this.addMergedBoxes(stripes, crossMaterial);

    // --- raised sidewalks (= block curbs) ---
    const blocks: BoxSpec[] = [];
    for (const x of BLOCK_CENTERS) {
      for (const z of BLOCK_CENTERS) {
        blocks.push({ w: BLOCK_HALF * 2, h: 0.28, d: BLOCK_HALF * 2, x, y: 0.12, z });
      }
    }
    this.addMergedBoxes(blocks, sidewalkMaterial);

    // Arena mode gets its objects from the authoritative server; spawning a
    // local set would collide with the server item ids.
    if (!this.options.authoritative) this.spawnWorldItems();
    this.cityGroup.matrixAutoUpdate = false;
    this.cityGroup.updateMatrix();
  }

  /** Merge many boxes into a single draw call (static city decoration). */
  private addMergedBoxes(boxes: BoxSpec[], material: THREE.Material) {
    const parts = boxes.map((box) => {
      const geometry = new THREE.BoxGeometry(box.w, box.h, box.d);
      geometry.translate(box.x, box.y, box.z);
      return geometry;
    });
    const merged = mergeGeometries(parts, false);
    parts.forEach((geometry) => geometry.dispose());
    if (!merged) return;
    const mesh = new THREE.Mesh(merged, material);
    mesh.receiveShadow = true;
    mesh.matrixAutoUpdate = false;
    mesh.updateMatrix();
    this.cityGroup.add(mesh);
  }

  /**
   * Populate the whole city before the round starts.
   *
   * Placement rules (see world.ts): static props go inside a block interior,
   * living/traffic kinds go into their lane. Both are checked against every
   * already placed item with the full footprint radius + margin, so nothing
   * can spawn inside anything else.
   */
  private spawnWorldItems() {
    let id = 0;
    const occupied: Array<{ x: number; z: number; radius: number }> = [];
    const add = (definition: ObjectDefinition, x: number, z: number, vx: number, vz: number, home: boolean) => {
      const item = this.makeItem(++id, definition, x, z, vx, vz);
      if (home) item.home = { x, z, definition };
      this.items.push(item);
      occupied.push({ x, z, radius: definition.footprint });
    };

    // 1) The planned city: buildings mid-plot, furniture on the plot border.
    for (const p of planCity(() => this.random.next(), (kind) => objectById(kind).footprint)) {
      add(objectById(p.id), p.x, p.z, 0, 0, true);
    }

    // 2) Life on the streets: walkers on the pavement, traffic in lanes, birds.
    const movers: Array<[string, number]> = [
      ["pedestrian", 80], ["dog", 18], ["cat", 12], ["pigeon", 22], ["cyclist", 12], ["taxi", 16],
    ];
    for (const [kind, count] of movers) {
      const definition = objectById(kind);
      if (definition.id !== kind) continue;
      for (let n = 0; n < count; n++) {
        const found = this.findSpot(definition, occupied);
        if (found) add(definition, found.x, found.z, found.vx, found.vz, false);
      }
    }
  }

  /** Builds the scene objects for one item and adds them to the city. */
  private makeItem(id: number, definition: ObjectDefinition, x: number, z: number, vx: number, vz: number): WorldItem {
    const root = this.createModel(definition);
    const y = definition.motion === "fly" ? definition.height * 1.4 : definition.height / 2 + 0.18;
    root.position.set(x, y, z);
    root.rotation.y = vx !== 0 || vz !== 0 ? yawFor(vx, vz) : layoutYaw(definition.id, x, z);
    root.matrixAutoUpdate = false;
    root.updateMatrix();
    this.cityGroup.add(root);
    const shadow = this.attachBlob(definition, x, z);
    return { id, definition, root, shadow, x, z, tx: x, tz: z, vx, vz, falling: false, consumed: false };
  }

  private createModel(definition: ObjectDefinition): THREE.Group {
    const group = new THREE.Group();
    let geometry = this.modelGeometries.get(definition.kind);
    if (!geometry) {
      geometry = this.buildModelGeometry(definition);
      this.modelGeometries.set(definition.kind, geometry);
    }
    if (ANIMATED_MODELS.has(definition.model)) {
      // Drawn by the instanced crowd; the group only carries the transform.
      group.userData.phase = this.random.next() * Math.PI * 2;
      group.userData.amp = definition.model === "bird" ? 1 : 0;
      const shared = geometry;
      const wheelR = definition.model === "taxi" ? definition.footprint * 0.14 * 1.2 : definition.footprint * 0.42;
      const stride = STRIDE[definition.model] ?? 1;
      this.crowd.add(
        definition.kind,
        () => shared,
        {
          bob: definition.model === "person" ? definition.height * 0.035 : definition.model === "dog" || definition.model === "cat" ? definition.height * 0.05 : 0,
          // wheel angle = distance / r; gait phase = distance * stride
          wheelK: definition.model === "taxi" || definition.model === "bike" ? 1 / (wheelR * stride) : 0,
        },
        group,
        new THREE.Color(SHIRTS[Math.floor(this.random.next() * SHIRTS.length)]!),
      );
      return group;
    }
    // Drawn by the prop batch. The group only carries the transform, same as
    // animated crowd roots, so hundreds of buildings stay one draw call per kind.
    this.props.add(definition.kind, geometry, this.propMaterial, group, definition.height >= 1.4);
    return group;
  }

  /**
   * Build one merged, vertex-coloured geometry per kind. Every prop is a
   * single draw call and the geometry is shared by all instances of that
   * kind, which is what makes hundreds of objects viable on a phone.
   */
  private buildModelGeometry(definition: ObjectDefinition): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    const DARK = 0x243140;
    const GLASS = 0xbde8f7;
    const primary = definition.color;
    const accent = definition.accent;
    const f = definition.footprint;
    const h = definition.height;
    const SKIN = 0xf1c7a0;
    const WHITE = 0xffffff;

    const add = (
      geometry: THREE.BufferGeometry,
      color: number,
      x: number,
      y: number,
      z: number,
      rot?: [number, number, number],
      anim?: { part: number; pivot?: [number, number, number]; tint?: boolean },
    ) => {
      const g = geometry.index ? geometry.toNonIndexed() : geometry;
      if (g !== geometry) geometry.dispose();
      if (rot) {
        if (rot[0]) g.rotateX(rot[0]);
        if (rot[1]) g.rotateY(rot[1]);
        if (rot[2]) g.rotateZ(rot[2]);
      }
      g.translate(x, y, z);
      const count = g.getAttribute("position").count;
      const colors = new Float32Array(count * 3);
      const tint = new THREE.Color(color);
      for (let index = 0; index < count; index++) {
        colors[index * 3] = tint.r;
        colors[index * 3 + 1] = tint.g;
        colors[index * 3 + 2] = tint.b;
      }
      g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
      const part = new Float32Array(count).fill(anim?.part ?? 0);
      const pivot = new Float32Array(count * 3);
      const [px, py, pz] = anim?.pivot ?? [0, 0, 0];
      for (let index = 0; index < count; index++) {
        pivot[index * 3] = px;
        pivot[index * 3 + 1] = py - h / 2; // same centring as the vertices below
        pivot[index * 3 + 2] = pz;
      }
      g.setAttribute("aPart", new THREE.BufferAttribute(part, 1));
      g.setAttribute("aPivot", new THREE.BufferAttribute(pivot, 3));
      g.setAttribute("aTintMask", new THREE.BufferAttribute(new Float32Array(count).fill(anim?.tint ? 1 : 0), 1));
      parts.push(g);
    };

    switch (definition.model) {
      case "cone":
        add(new THREE.ConeGeometry(f * 0.75, h, 14), primary, 0, h / 2, 0);
        add(new THREE.CylinderGeometry(f, f, 0.08, 14), DARK, 0, 0.04, 0);
        add(new THREE.TorusGeometry(f * 0.42, 0.05, 6, 14), accent, 0, h * 0.42, 0, [Math.PI / 2, 0, 0]);
        break;
      case "cylinder":
        add(new THREE.CylinderGeometry(f * 0.72, f * 0.8, h, 14), primary, 0, h / 2, 0);
        add(new THREE.CylinderGeometry(f * 0.8, f * 0.8, 0.1, 14), accent, 0, h + 0.02, 0);
        break;
      case "crate":
        add(new THREE.BoxGeometry(f * 1.5, h, f * 1.5), primary, 0, h / 2, 0);
        add(new THREE.BoxGeometry(f * 1.55, 0.08, 0.08), accent, 0, h * 0.55, f * 0.76);
        break;
      case "lamp":
        add(new THREE.CylinderGeometry(0.09, 0.12, h * 0.86, 8), DARK, 0, h * 0.43, 0);
        add(new THREE.SphereGeometry(f * 0.38, 10, 8), accent, 0, h * 0.9, 0);
        add(new THREE.CylinderGeometry(f * 0.5, f * 0.6, 0.15, 10), DARK, 0, 0.075, 0);
        break;
      case "bench":
        add(new THREE.BoxGeometry(f * 1.8, 0.18, f * 0.55), primary, 0, h * 0.52, 0);
        add(new THREE.BoxGeometry(f * 1.8, h * 0.5, 0.16), primary, 0, h * 0.72, f * 0.3);
        add(new THREE.BoxGeometry(0.14, h * 0.5, 0.14), DARK, -f * 0.65, h * 0.25, 0);
        add(new THREE.BoxGeometry(0.14, h * 0.5, 0.14), DARK, f * 0.65, h * 0.25, 0);
        break;
      case "tree":
        add(new THREE.CylinderGeometry(f * 0.16, f * 0.22, h * 0.5, 8), DARK, 0, h * 0.25, 0);
        add(new THREE.IcosahedronGeometry(f * 0.78, 1), primary, 0, h * 0.68, 0);
        add(new THREE.IcosahedronGeometry(f * 0.5, 1), primary, -f * 0.26, h * 0.82, 0);
        break;
      case "car":
      case "van":
      case "taxi": {
        const length = f * (definition.model === "van" ? 1.85 : 1.7);
        add(new THREE.BoxGeometry(length, h * 0.48, f * 1.2), primary, 0, h * 0.38, 0);
        add(
          new THREE.BoxGeometry(length * 0.55, h * 0.42, f * 1.08),
          definition.model === "van" ? primary : GLASS,
          -length * 0.08,
          h * 0.76,
          0,
        );
        if (definition.model === "taxi") {
          add(new THREE.BoxGeometry(length * 0.3, h * 0.16, f * 0.5), accent, length * 0.02, h * 0.98, 0);
        }
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
          const wx = sx * length * 0.32;
          const wz = sz * f * 0.58;
          add(
            new THREE.CylinderGeometry(f * 0.14 * 1.2, f * 0.14 * 1.2, 0.16, 10),
            DARK,
            wx, h * 0.22, wz,
            [Math.PI / 2, 0, 0],
            { part: Part.Wheel, pivot: [wx, h * 0.22, wz] },
          );
          // hub cap so the spin is visible
          add(new THREE.BoxGeometry(f * 0.22, f * 0.05, 0.18), 0xc9d2dc, wx, h * 0.22, wz, undefined, { part: Part.Wheel, pivot: [wx, h * 0.22, wz] });
        }
        break;
      }
      case "bus": {
        const length = f * 2.0;
        add(new THREE.BoxGeometry(length, h * 0.62, f * 1.1), primary, 0, h * 0.42, 0);
        add(new THREE.BoxGeometry(length * 0.9, h * 0.2, f * 1.12), GLASS, 0, h * 0.72, 0);
        add(new THREE.BoxGeometry(length * 0.98, h * 0.12, f * 1.06), accent, 0, h * 0.2, 0);
        for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
          add(
            new THREE.CylinderGeometry(f * 0.16, f * 0.16, 0.16, 10),
            DARK,
            sx * length * 0.34, h * 0.18, sz * f * 0.55,
            [Math.PI / 2, 0, 0],
          );
        }
        break;
      }
      case "kiosk":
        add(new THREE.BoxGeometry(f * 1.5, h * 0.78, f * 1.25), primary, 0, h * 0.39, 0);
        add(new THREE.ConeGeometry(f * 1.18, h * 0.28, 4), accent, 0, h * 0.92, 0, [0, Math.PI / 4, 0]);
        add(new THREE.BoxGeometry(f * 0.8, h * 0.25, 0.08), DARK, 0, h * 0.48, f * 0.64);
        break;
      case "cart":
        add(new THREE.BoxGeometry(f * 1.3, h * 0.34, f * 0.9), primary, 0, h * 0.34, 0);
        add(new THREE.ConeGeometry(f * 1.1, h * 0.22, 8), accent, 0, h * 0.86, 0);
        add(new THREE.CylinderGeometry(0.06, 0.06, h * 0.5, 6), DARK, 0, h * 0.62, 0);
        for (const sx of [-1, 1]) {
          add(new THREE.CylinderGeometry(f * 0.26, f * 0.26, 0.1, 10), DARK, sx * f * 0.45, h * 0.16, 0, [Math.PI / 2, 0, 0]);
        }
        break;
      case "fountain":
        add(new THREE.CylinderGeometry(f, f * 1.05, h * 0.42, 20), primary, 0, h * 0.21, 0);
        add(new THREE.CylinderGeometry(f * 0.86, f * 0.86, 0.08, 20), accent, 0, h * 0.46, 0);
        add(new THREE.CylinderGeometry(f * 0.16, f * 0.22, h * 0.5, 10), primary, 0, h * 0.7, 0);
        add(new THREE.SphereGeometry(f * 0.22, 10, 8), accent, 0, h * 0.98, 0);
        break;
      case "person": {
        // Forward is +x. Two legs, two arms swing around hip/shoulder joints.
        const hip = h * 0.46;
        const shoulder = h * 0.76;
        for (const [sz, leg] of [[-1, Part.LegA], [1, Part.LegB]] as const) {
          const z = sz * f * 0.2;
          add(new THREE.BoxGeometry(f * 0.3, hip, f * 0.28), 0x2f3b52, 0, hip / 2, z, undefined, { part: leg, pivot: [0, hip, z] });
          add(new THREE.BoxGeometry(f * 0.46, h * 0.05, f * 0.3), 0x1d232e, f * 0.07, h * 0.025, z, undefined, { part: leg, pivot: [0, hip, z] });
        }
        add(new THREE.BoxGeometry(f * 0.5, h * 0.34, f * 0.78), WHITE, 0, h * 0.62, 0, undefined, { part: Part.Static, tint: true });
        for (const [sz, arm] of [[-1, Part.ArmA], [1, Part.ArmB]] as const) {
          const z = sz * f * 0.52;
          add(new THREE.BoxGeometry(f * 0.2, h * 0.3, f * 0.2), WHITE, 0, shoulder - h * 0.15, z, undefined, { part: arm, pivot: [0, shoulder, z], tint: true });
          add(new THREE.SphereGeometry(f * 0.11, 6, 5), SKIN, 0, shoulder - h * 0.31, z, undefined, { part: arm, pivot: [0, shoulder, z] });
        }
        add(new THREE.SphereGeometry(f * 0.3, 12, 10), SKIN, 0, h * 0.88, 0);
        add(new THREE.SphereGeometry(f * 0.31, 12, 6, 0, Math.PI * 2, 0, Math.PI / 2), accent, -f * 0.03, h * 0.9, 0);
        break;
      }
      case "dog":
      case "cat": {
        const dog = definition.model === "dog";
        const legH = h * (dog ? 0.42 : 0.34);
        const body = dog ? [f * 1.5, h * 0.42, f * 0.8] : [f * 1.4, h * 0.36, f * 0.7];
        add(new THREE.BoxGeometry(body[0]!, body[1]!, body[2]!), primary, 0, legH + body[1]! / 2, 0);
        const headX = body[0]! * 0.55;
        if (dog) {
          add(new THREE.BoxGeometry(f * 0.55, h * 0.42, f * 0.55), primary, headX, legH + h * 0.42, 0);
          add(new THREE.BoxGeometry(f * 0.3, h * 0.18, f * 0.32), accent, headX + f * 0.36, legH + h * 0.34, 0);
          for (const sz of [-1, 1]) add(new THREE.BoxGeometry(f * 0.14, h * 0.24, f * 0.1), accent, headX - f * 0.1, legH + h * 0.62, sz * f * 0.2);
        } else {
          add(new THREE.SphereGeometry(f * 0.34, 10, 8), primary, headX, legH + h * 0.5, 0);
          for (const sz of [-1, 1]) add(new THREE.ConeGeometry(f * 0.12, f * 0.26, 6), primary, headX, legH + h * 0.78, sz * f * 0.18);
        }
        // Diagonal leg pairs move together (trot).
        for (const [sx, sz, leg] of [[1, -1, Part.LegA], [-1, 1, Part.LegA], [1, 1, Part.LegB], [-1, -1, Part.LegB]] as const) {
          const x = sx * body[0]! * 0.36;
          const z = sz * body[2]! * 0.32;
          add(new THREE.BoxGeometry(f * 0.16, legH, f * 0.16), DARK, x, legH / 2, z, undefined, { part: leg, pivot: [x, legH, z] });
        }
        const tailX = -body[0]! / 2;
        add(new THREE.BoxGeometry(f * (dog ? 0.4 : 0.6), f * 0.1, f * 0.1), accent, tailX - f * (dog ? 0.18 : 0.28), legH + body[1]! * 0.8, 0,
          [0, 0, dog ? 0.6 : 0.9], { part: Part.Tail, pivot: [tailX, legH + body[1]! * 0.8, 0] });
        break;
      }
      case "bird": {
        add(new THREE.SphereGeometry(f * 0.5, 10, 8), primary, 0, h * 0.6, 0);
        add(new THREE.SphereGeometry(f * 0.28, 8, 6), accent, f * 0.45, h * 0.92, 0);
        add(new THREE.ConeGeometry(f * 0.12, f * 0.3, 6), 0xf5b83d, f * 0.72, h * 0.9, 0, [0, 0, -Math.PI / 2]);
        for (const [sz, wing] of [[1, Part.WingL], [-1, Part.WingR]] as const) {
          add(new THREE.BoxGeometry(f * 0.55, f * 0.05, f * 0.7), accent, -f * 0.05, h * 0.66, sz * f * 0.42, undefined,
            { part: wing, pivot: [0, h * 0.66, sz * f * 0.08] });
        }
        break;
      }
      case "bike": {
        const r = f * 0.42;
        for (const sx of [-1, 1]) {
          add(new THREE.TorusGeometry(r, f * 0.06, 6, 16), DARK, sx * f * 0.62, r, 0, undefined, { part: Part.Wheel, pivot: [sx * f * 0.62, r, 0] });
          add(new THREE.BoxGeometry(r * 1.8, f * 0.04, f * 0.04), 0xc9d2dc, sx * f * 0.62, r, 0, undefined, { part: Part.Wheel, pivot: [sx * f * 0.62, r, 0] });
        }
        add(new THREE.BoxGeometry(f * 1.15, f * 0.08, f * 0.08), primary, 0, f * 0.62, 0);
        add(new THREE.BoxGeometry(f * 0.08, f * 0.5, f * 0.08), primary, f * 0.5, f * 0.8, 0);
        const seat = f * 0.95;
        for (const [sz, leg] of [[-1, Part.LegA], [1, Part.LegB]] as const) {
          const z = sz * f * 0.14;
          add(new THREE.BoxGeometry(f * 0.14, seat * 0.7, f * 0.14), 0x2f3b52, f * 0.05, seat - seat * 0.35, z, undefined, { part: leg, pivot: [0, seat, z] });
        }
        add(new THREE.BoxGeometry(f * 0.42, h * 0.32, f * 0.5), WHITE, -f * 0.05, h * 0.64, 0, [0, 0, -0.25], { part: Part.Static, tint: true });
        add(new THREE.SphereGeometry(f * 0.2, 10, 8), SKIN, f * 0.1, h * 0.86, 0);
        add(new THREE.SphereGeometry(f * 0.22, 10, 6, 0, Math.PI * 2, 0, Math.PI / 2), accent, f * 0.1, h * 0.88, 0);
        break;
      }
      case "house":
        add(new THREE.BoxGeometry(f * 1.45, h * 0.72, f * 1.35), primary, 0, h * 0.36, 0);
        add(new THREE.ConeGeometry(f * 1.12, h * 0.34, 4), accent, 0, h * 0.86, 0, [0, Math.PI / 4, 0]);
        for (const x of [-f * 0.38, f * 0.38]) add(new THREE.BoxGeometry(f * 0.22, h * 0.2, 0.08), GLASS, x, h * 0.46, f * 0.68);
        break;
      case "tank":
        add(new THREE.BoxGeometry(f * 1.7, h * 0.3, f), DARK, 0, h * 0.2, 0);
        add(new THREE.CylinderGeometry(f * 0.12, f * 0.12, f * 2.1, 10), primary, 0, h * 0.38, f * 0.42, [Math.PI / 2, 0, 0]);
        add(new THREE.BoxGeometry(f * 1.0, h * 0.3, f * 0.8), primary, 0, h * 0.64, 0);
        add(new THREE.CylinderGeometry(f * 0.4, f * 0.5, h * 0.32, 10), accent, 0, h * 0.86, 0);
        break;
      case "house_big":
        add(new THREE.BoxGeometry(f * 1.4, h * 0.76, f * 1.2), primary, 0, h * 0.38, 0);
        add(new THREE.BoxGeometry(f * 1.55, h * 0.16, f * 1.35), accent, 0, h * 0.8, 0);
        add(new THREE.ConeGeometry(f * 1.12, h * 0.26, 4), accent, 0, h * 0.98, 0, [0, Math.PI / 4, 0]);
        for (let wy = h * 0.24; wy < h * 0.72; wy += h * 0.22) {
          add(new THREE.BoxGeometry(f * 0.18, h * 0.14, 0.12), GLASS, -f * 0.32, wy, f * 0.62);
          add(new THREE.BoxGeometry(f * 0.18, h * 0.14, 0.12), GLASS, f * 0.32, wy, f * 0.62);
        }
        break;
      case "tower":
      case "skyscraper":
        add(new THREE.BoxGeometry(f * 1.2, h, f * 1.1), primary, 0, h / 2, 0);
        for (let y = h * 0.12; y < h * 0.92; y += h * 0.14) {
          add(new THREE.BoxGeometry(f * 1.24, 0.1, f * 1.14), accent, 0, y, 0);
        }
        add(new THREE.BoxGeometry(f * 0.24, h * 0.1, f * 0.24), DARK, 0, h + 0.02, 0);
        break;
      case "block":
      case "city_block":
        add(new THREE.BoxGeometry(f * 1.3, h * 0.7, f * 1.1), primary, 0, h * 0.35, 0);
        add(new THREE.BoxGeometry(f * 0.8, h, f * 0.7), accent, -f * 0.18, h / 2, f * 0.1);
        for (let wy = h * 0.18; wy < h * 0.9; wy += h * 0.16) {
          add(new THREE.BoxGeometry(f * 0.16, h * 0.1, 0.12), GLASS, f * 0.42, wy, -f * 0.3);
        }
        break;
    }

    const merged = mergeGeometries(parts, false);
    parts.forEach((geometry) => geometry.dispose());
    if (!merged) return new THREE.BoxGeometry(f, h, f).translate(0, 0, 0);
    // Models are authored with their floor at y=0; centre them so the group
    // origin sits at mid-height (same convention as before).
    merged.translate(0, -h / 2, 0);
    merged.computeBoundingSphere();
    return merged;
  }

  private buildHole() {
    this.scene.add(this.holeGroup, this.remoteHoleGroup);
    this.playerVisual = new HoleVisual(skinById(this.options.skinId), { effects: this.fx });
    this.holeGroup.add(this.playerVisual.group);
    this.buildPopups();
  }

  private buildPhysicsRim(force = false) {
    if (!this.physics || (!force && Math.abs(this.rimRadius - this.hole.radius) < 0.32)) return;
    if (this.rimBody) this.physics.removeRigidBody(this.rimBody);
    const body = this.physics.createRigidBody(RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(this.hole.x, 0, this.hole.z));
    const segments = 24;
    const tangentHalf = (Math.PI * this.hole.radius / segments) * 0.92;
    for (let index = 0; index < segments; index++) {
      const angle = (index / segments) * Math.PI * 2;
      const radial = this.hole.radius + 0.02;
      const descriptor = RAPIER.ColliderDesc.cuboid(tangentHalf, 0.26, 0.12)
        .setTranslation(Math.cos(angle) * radial, -0.18, Math.sin(angle) * radial)
        .setRotation({ x: 0, y: Math.sin(-angle / 2), z: 0, w: Math.cos(-angle / 2) })
        .setFriction(0.72)
        .setRestitution(0.05);
      this.physics.createCollider(descriptor, body);
    }
    this.rimBody = body;
    this.rimRadius = this.hole.radius;
  }

  private activateItem(item: WorldItem) {
    if (!this.physics || item.falling || item.consumed) return;
    item.falling = true;
    item.shadow.visible = false;
    const position = item.root.position;
    const body = this.physics.createRigidBody(
      RAPIER.RigidBodyDesc.dynamic()
        .setTranslation(position.x, position.y, position.z)
        .setRotation({ x: item.root.quaternion.x, y: item.root.quaternion.y, z: item.root.quaternion.z, w: item.root.quaternion.w })
        .setLinearDamping(0.08)
        .setAngularDamping(0.06),
    );
    const d = item.definition;
    const collider = d.model === "tree" || d.model === "tank"
      ? RAPIER.ColliderDesc.cylinder(d.height * 0.48, d.footprint * 0.5)
      : RAPIER.ColliderDesc.cuboid(d.footprint * 0.5, d.height * 0.48, d.footprint * 0.5);
    collider.setDensity(Math.max(0.25, Math.min(8, Math.sqrt(d.mass) / 18))).setFriction(0.66).setRestitution(0.08);
    this.physics.createCollider(collider, body);
    // Straight drop: no impulse towards the hole centre (no "magnet").
    body.applyImpulse({ x: 0, y: -d.mass * 0.0012, z: 0 }, true);
    body.applyTorqueImpulse({
      x: this.random.range(-1, 1) * Math.sqrt(d.mass) * 0.08,
      y: this.random.range(-1, 1) * Math.sqrt(d.mass) * 0.04,
      z: this.random.range(-1, 1) * Math.sqrt(d.mass) * 0.08,
    }, true);
    item.body = body;
  }

  private updateItems(dt: number) {
    const simulateEat = !this.options.authoritative && this.alive;
    for (const item of this.items) {
      if (item.consumed) continue;
      if (!item.falling) {
        let moving = item.vx !== 0 || item.vz !== 0;
        if (this.options.authoritative) {
          // The server owns moving items; glide towards its snapshots so
          // traffic does not visibly jump at the 10 Hz snapshot rate.
          // Resting props stay put and keep the matrix written at spawn.
          const dx = item.tx - item.x;
          const dz = item.tz - item.z;
          if (dx * dx + dz * dz > 1e-4) {
            const k = 1 - Math.exp(-dt * 14);
            item.x += dx * k;
            item.z += dz * k;
            moving = true;
          }
        } else if (moving) {
          this.advanceItemMotion(item, dt);
        }
        if (moving) {
          const baseY = item.definition.motion === "fly"
            ? item.definition.height * 1.4
            : item.definition.height / 2 + 0.18;
          this.animateGait(item, dt);
          item.root.position.set(item.x, baseY, item.z);
          item.shadow.position.set(item.x, 0.2, item.z);
        }

        if (!simulateEat) continue;
        const reach = this.hole.radius + item.definition.footprint;
        if (!withinReach(item.x, item.z, this.hole.x, this.hole.z, reach)) continue;
        const distance = Math.hypot(item.x - this.hole.x, item.z - this.hole.z);
        // No magnet: an object only drops once it really fits inside.
        if (canFitInHole(this.hole.radius, item.definition.footprint, distance)) this.activateItem(item);
        continue;
      }

      const body = item.body;
      if (!body) continue;
      const position = body.translation();
      const rotation = body.rotation();
      item.root.position.set(position.x, position.y, position.z);
      item.root.quaternion.set(rotation.x, rotation.y, rotation.z, rotation.w);
      // Living things flail while they fall.
      if (item.root.userData.amp !== undefined) {
        item.root.userData.phase += dt * 14;
        item.root.userData.amp = 1.6;
      }

      // Swallowed only once it has fallen completely out of sight below
      // the abyss floor, not the moment it tips over the rim.
      if (position.y + item.definition.height < -SWALLOW_DEPTH) this.consumeItem(item);
    }

  }

  /** Gait phase follows the distance actually travelled (no foot sliding). */
  private animateGait(item: WorldItem, dt: number) {
    const data = item.root.userData;
    if (data.phase === undefined) return;
    const model = item.definition.model;
    if (model === "bird") {
      data.amp = 1;
      return;
    }
    const moved = Math.hypot(item.x - item.root.position.x, item.z - item.root.position.z);
    const travelled = moved < 3 ? moved : 0; // ignore wrap-around jumps
    data.phase += travelled * (STRIDE[model] ?? 1);
    const speed = dt > 0 ? travelled / dt : 0;
    const target = speed > 0.2 ? 1 : 0;
    data.amp += (target - data.amp) * Math.min(1, dt * 8);
  }

  /** Advance a living/traffic item along its lane and wrap at the map edge. */
  private advanceItemMotion(item: WorldItem, dt: number) {
    if (item.vx === 0 && item.vz === 0) return;
    const next = advanceLane(item.x, item.z, item.vx, item.vz, dt);
    item.x = next.x;
    item.z = next.z;
    item.tx = next.x;
    item.tz = next.z;
  }

  private consumeItem(item: WorldItem) {
    if (item.consumed) return;
    item.consumed = true;
    if (item.body && this.physics) this.physics.removeRigidBody(item.body);
    item.root.visible = false;
    item.shadow.visible = false;
    if (!this.options.authoritative) {
      this.score += item.definition.score;
      this.eaten++;
      this.hole.targetRadius = Math.min(MAX_RADIUS, this.hole.targetRadius + item.definition.growth);
      if (this.bots.length) item.respawnAt = this.elapsed + this.respawnDelay(item.definition);
    }
    this.combo++;
    this.comboLife = 1.15;
    this.shake = Math.min(0.5, 0.12 + item.definition.tier * 0.035);
    this.spawnBurst(item.definition);
    if (!this.options.authoritative) {
      const mult = this.combo > 2 ? ` ×${this.combo}` : "";
      this.popScore(this.hole.x, this.hole.z, `+${item.definition.score}${mult}`, item.definition.tier >= 4 ? "#ffd761" : "#ffffff", 0.7 + item.definition.tier * 0.08);
    }
    // Quiet, tier-scaled swallow sound; rapid-fire throttling + pitch
    // wobble live in audio.playSfx so streaks cannot stack into a buzz.
    void audio.playSfx(item.definition.tier >= 3 ? "eat_big" : "eat", Math.min(0.5, 0.16 + item.definition.tier * 0.045));
  }

  private spawnBurst(definition: ObjectDefinition, x = this.hole.x, z = this.hole.z, visual = this.playerVisual) {
    const count = Math.min(22, 6 + definition.tier * 2);
    const colors = [definition.color, definition.accent];
    for (let index = 0; index < count; index++) {
      const angle = this.random.range(0, Math.PI * 2);
      const speed = this.random.range(2, 4.5 + definition.tier * 0.5);
      this.fx.solid.emit({
        x, y: 0.2, z,
        vx: Math.cos(angle) * speed, vy: this.random.range(3, 6), vz: Math.sin(angle) * speed,
        color: colors[index % 2]!, size: 0.28 + definition.tier * 0.05, sizeEnd: 0.2,
        life: 0.7, gravity: 14, drag: 0.8,
      });
    }
    // dust puff for bigger objects
    if (definition.tier >= 3) {
      for (let index = 0; index < 8; index++) {
        const angle = (index / 8) * Math.PI * 2;
        this.fx.solid.emit({
          x: x + Math.cos(angle) * 1.2, y: 0.4, z: z + Math.sin(angle) * 1.2,
          vx: Math.cos(angle) * 2.2, vy: 0.6, vz: Math.sin(angle) * 2.2,
          color: 0xd9d2c4, size: 1.4 + definition.tier * 0.25, sizeEnd: 2.2, life: 0.9, drag: 2, alpha: 0.45,
        });
      }
    }
    visual?.bump(0.4 + definition.tier * 0.12);
    visual?.burst(4 + definition.tier * 2);
  }

  private buildPopups() {
    for (let i = 0; i < 10; i++) {
      const canvas = document.createElement("canvas");
      canvas.width = 256;
      canvas.height = 96;
      const texture = new THREE.CanvasTexture(canvas);
      texture.colorSpace = THREE.SRGBColorSpace;
      const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true }));
      sprite.visible = false;
      sprite.renderOrder = 12;
      this.effectsGroup.add(sprite);
      this.popups.push({ sprite, canvas, texture, life: 0, vy: 0 });
    }
  }

  /** Floating "+120" above a point. */
  private popScore(x: number, z: number, text: string, color: string, scale = 1) {
    const popup = this.popups[this.popupCursor];
    if (!popup) return;
    this.popupCursor = (this.popupCursor + 1) % this.popups.length;
    const ctx = popup.canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, 256, 96);
    ctx.font = "900 64px 'Barlow Condensed', system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineWidth = 10;
    ctx.strokeStyle = "rgba(8,12,22,0.9)";
    ctx.strokeText(text, 128, 50);
    ctx.fillStyle = color;
    ctx.fillText(text, 128, 50);
    popup.texture.needsUpdate = true;
    popup.sprite.visible = true;
    popup.sprite.position.set(x, 2.5, z);
    const s = (2.6 + this.hole.radius * 0.35) * scale;
    popup.sprite.scale.set(s * 2.66, s, 1);
    popup.life = 1;
    popup.vy = 3;
  }

  private updatePopups(dt: number) {
    for (const popup of this.popups) {
      if (popup.life <= 0) continue;
      popup.life -= dt * 1.1;
      popup.sprite.position.y += popup.vy * dt;
      popup.vy *= 1 - dt * 2;
      const material = popup.sprite.material;
      material.opacity = Math.min(1, popup.life * 3);
      if (popup.life <= 0) popup.sprite.visible = false;
    }
  }

  /** Big ring + sparks when one hole swallows another. */
  private shockwave(x: number, z: number, radius: number, color: string) {
    this.fx.glow.emit({ x, y: 0.4, z, color, size: radius * 2.4, sizeEnd: 3.2, life: 0.6, shape: Shape.Ring, alpha: 0.9 });
    for (let i = 0; i < 24; i++) {
      const a = (i / 24) * Math.PI * 2;
      this.fx.glow.emit({
        x: x + Math.cos(a) * radius, y: 0.4, z: z + Math.sin(a) * radius,
        vx: Math.cos(a) * 9, vy: 2 + this.random.next() * 3, vz: Math.sin(a) * 9,
        color, size: 0.6, sizeEnd: 0.1, life: 0.7, drag: 2, gravity: 4, shape: Shape.Spark, spin: 5,
      });
    }
  }

  private bindInput() {
    this.canvas.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.canvas.addEventListener("contextmenu", this.preventDefault);
  }

  private unbindInput() {
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.canvas.removeEventListener("contextmenu", this.preventDefault);
  }

  private preventDefault = (event: Event) => event.preventDefault();
  private onPointerDown = (event: PointerEvent) => {
    this.pointerActive = true;
    this.pointerAnchor.set(event.clientX, event.clientY);
    this.pointerCurrent.copy(this.pointerAnchor);
    this.canvas.setPointerCapture?.(event.pointerId);
  };
  private onPointerMove = (event: PointerEvent) => {
    if (!this.pointerActive) return;
    this.pointerCurrent.set(event.clientX, event.clientY);
  };
  private onPointerUp = () => {
    this.pointerActive = false;
    this.input.set(0, 0);
  };
  private onKeyDown = (event: KeyboardEvent) => {
    if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "w", "a", "s", "d", "W", "A", "S", "D"].includes(event.key)) {
      event.preventDefault();
      this.keyState.add(event.key.toLowerCase());
    }
  };
  private onKeyUp = (event: KeyboardEvent) => this.keyState.delete(event.key.toLowerCase());

  private updateInput(dt: number) {
    this.input.set(0, 0);
    if (this.keyState.has("a") || this.keyState.has("arrowleft")) this.input.x -= 1;
    if (this.keyState.has("d") || this.keyState.has("arrowright")) this.input.x += 1;
    if (this.keyState.has("w") || this.keyState.has("arrowup")) this.input.y -= 1;
    if (this.keyState.has("s") || this.keyState.has("arrowdown")) this.input.y += 1;

    if (this.pointerActive) {
      const dx = this.pointerCurrent.x - this.pointerAnchor.x;
      const dy = this.pointerCurrent.y - this.pointerAnchor.y;
      const length = Math.hypot(dx, dy);
      if (length > 8) this.input.set(dx / Math.max(70, length), dy / Math.max(70, length));
    }

    if (this.input.lengthSq() > 1) this.input.normalize();
    if (!this.alive) return;
    const speed = 12.5 / (1 + this.hole.radius * 0.035);
    this.hole.x = THREE.MathUtils.clamp(this.hole.x + this.input.x * speed * dt, -RAIL, RAIL);
    this.hole.z = THREE.MathUtils.clamp(this.hole.z + this.input.y * speed * dt, -RAIL, RAIL);
    if (this.options.authoritative && this.input.lengthSq() > 0) {
      this.targetAccumulator += dt;
      if (this.targetAccumulator >= 0.05) {
        this.targetAccumulator = 0;
        this.options.onTarget?.(this.hole.x, this.hole.z);
      }
    }
  }

  private updateHole(dt: number) {
    this.hole.radius = THREE.MathUtils.damp(this.hole.radius, this.hole.targetRadius, 4.5, dt);
    this.holeGroup.visible = this.alive;
    this.playerVisual?.setVisible(this.alive);
    this.playerVisual?.update(dt, this.hole.x, this.hole.z, this.hole.radius);
    if (this.rimBody) this.rimBody.setNextKinematicTranslation({ x: this.hole.x, y: 0, z: this.hole.z });
    this.buildPhysicsRim();
  }

  /** Push every open hole into the ground shader (player first). */
  private syncHoleUniforms() {
    const slots = this.holeUniforms.holes.value;
    let n = 0;
    const put = (x: number, z: number, r: number) => {
      if (n < MAX_HOLES) slots[n++]!.set(x, z, r);
    };
    if (this.alive) put(this.hole.x, this.hole.z, this.hole.radius);
    for (const bot of this.bots) if (bot.alive) put(bot.x, bot.z, bot.radius);
    if (this.remoteVisuals.size) {
      // Nearest opponents first when the arena has more than fit.
      const remote = [...this.remoteVisuals.values()]
        .map((v) => ({ v, d: Math.hypot(v.x - this.hole.x, v.z - this.hole.z) }))
        .sort((a, b) => a.d - b.d);
      for (const { v } of remote) put(v.x, v.z, v.currentRadius);
    }
    while (n < MAX_HOLES) slots[n++]!.set(0, 0, 0);
  }

  private updateCamera(dt: number) {
    const portrait = this.camera.aspect < 0.9;
    const height = (portrait ? 30 : 25) + this.hole.radius * 1.55;
    this._camTarget.set(this.hole.x, height, this.hole.z + height * 0.64);
    this.camera.position.lerp(this._camTarget, 1 - Math.exp(-dt * 4.8));
    this._camLook.set(this.hole.x, 0, this.hole.z - (portrait ? 3.2 : 1));
    if (this.shake > 0) {
      this.shake = Math.max(0, this.shake - dt);
      this.camera.position.x += this.random.range(-this.shake, this.shake);
      this.camera.position.y += this.random.range(-this.shake, this.shake) * 0.35;
      this.camera.position.z += this.random.range(-this.shake, this.shake);
    }
    this.camera.lookAt(this._camLook);
    if (this.sun) {
      this.sun.position.set(this.hole.x - 38, 62, this.hole.z + 28);
      this.sun.target.position.set(this.hole.x, 0, this.hole.z);
    }
  }

  private standings(): { leaders?: LeaderEntry[]; rank?: number } {
    if (this.options.authoritative) return this.arenaLeaders ?? {};
    if (!this.bots.length) return {};
    const ranked = leaderboard([
      { name: this.options.playerName ?? "You", score: this.score, size: this.hole.radius * 2, me: true, color: this.options.skinColor },
      ...this.bots.map((bot) => ({ name: bot.name, score: bot.score, size: bot.radius * 2, me: false, color: bot.color })),
    ]);
    return { leaders: ranked.slice(0, 5), rank: ranked.findIndex((entry) => entry.me) + 1 };
  }

  private spawnBots(count: number) {
    this.bots = createBots(count, () => this.random.next(), RAIL - 4);
    this.bots.forEach((bot, index) => {
      const skin = botSkin(index, this.options.skinId);
      bot.color = skin.color;
      const visual = new HoleVisual(skin, { effects: this.fx, particleScale: 0.45 });
      const label = this.makeLabel(bot.name, skin.color);
      this.remoteHoleGroup.add(visual.group, label);
      this.botViews.set(bot.id, { visual, label });
    });
  }

  private makeLabel(text: string, color: string): THREE.Sprite {
    const canvas = document.createElement("canvas");
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.font = "bold 34px system-ui, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.lineWidth = 7;
      ctx.strokeStyle = "rgba(10,14,24,0.85)";
      ctx.strokeText(text, 128, 32);
      ctx.fillStyle = color;
      ctx.fillText(text, 128, 32);
    }
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const sprite = new THREE.Sprite(new THREE.SpriteMaterial({ map: texture, depthTest: false, transparent: true }));
    sprite.scale.set(6, 1.5, 1);
    sprite.renderOrder = 10;
    return sprite;
  }

  private meContestant(): Contestant {
    return {
      id: 0, name: this.options.playerName ?? "You", x: this.hole.x, z: this.hole.z,
      radius: this.hole.radius, targetRadius: this.hole.targetRadius, score: this.score,
      eaten: this.eaten, alive: this.alive, respawnAt: this.respawnAt,
    };
  }

  private pushFeed(text: string) {
    this.feed = { text, id: ++this.feedId };
  }

  private updateBots(dt: number) {
    const rand = () => this.random.next();
    const me = this.meContestant();
    const contestants: Contestant[] = [me, ...this.bots];
    let edible: BotItem[] | undefined;

    for (const bot of this.bots) {
      if (!bot.alive) {
        if (this.elapsed >= bot.respawnAt) this.respawnContestant(bot);
        continue;
      }
      if (this.elapsed >= bot.nextPlan) {
        bot.nextPlan = this.elapsed + 0.25 * (1.8 - bot.skill) + rand() * 0.1;
        // Rebuild the edible list once per planning wave, not every frame.
        edible ??= this.items.flatMap((item) => item.falling || item.consumed ? [] : [{ id: item.id, x: item.x, z: item.z, definition: item.definition }]);
        planBot(bot, contestants, edible, rand, RAIL);
      }
      moveBot(bot, dt);
      bot.radius = THREE.MathUtils.damp(bot.radius, bot.targetRadius, 4.5, dt);
      for (const item of this.items) {
        if (item.falling || item.consumed) continue;
        const reach = bot.radius + item.definition.footprint;
        if (!withinReach(item.x, item.z, bot.x, bot.z, reach)) continue;
        const d = Math.hypot(item.x - bot.x, item.z - bot.z);
        if (!canFitInHole(bot.radius, item.definition.footprint, d)) continue;
        item.consumed = true;
        item.shadow.visible = false;
        this.sinking.push({ item, bot, t: 0 });
      }
    }

    // Hole-vs-hole swallowing.
    for (let a = 0; a < contestants.length; a++) {
      for (let b = a + 1; b < contestants.length; b++) {
        const result = duel(contestants[a]!, contestants[b]!);
        if (!result) continue;
        const [winner, loser] = result;
        const winnerColor = winner === me ? skinById(this.options.skinId).trail : (winner as Bot).color ?? "#ffffff";
        this.shockwave(loser.x, loser.z, Math.max(2, loser.radius), winnerColor);
        if (winner === me) this.popScore(loser.x, loser.z, `+${Math.floor(loser.score * 0.5) + 150}`, "#ff6b8b", 1.4);
        applyKill(winner, loser, this.elapsed, loser === me);
        const t = this.options.feedText;
        if (loser === me) {
          void audio.playSfx("end", 0.45);
          this.shake = 0.6;
          this.pushFeed((t?.killedBy ?? "{a} swallowed you!").replace("{a}", winner.name));
        } else if (winner === me) {
          this.kills++;
          this.shake = 0.45;
          void audio.playSfx("eat_big", 0.5);
          this.pushFeed((t?.kill ?? "You swallowed {b}!").replace("{b}", loser.name));
        } else {
          this.pushFeed((t?.you ?? "{a} swallowed {b}").replace("{a}", winner.name).replace("{b}", loser.name));
        }
      }
    }
    // Write the player's proxy back.
    this.score = me.score;
    this.hole.targetRadius = me.targetRadius;
    if (this.alive && !me.alive) {
      this.alive = false;
      this.respawnAt = me.respawnAt;
      this.hole.radius = me.radius;
      this.combo = 0;
    }
    if (!this.alive && this.elapsed >= this.respawnAt) {
      this.respawnContestant(me);
      this.hole.x = me.x;
      this.hole.z = me.z;
      this.alive = true;
      void audio.playSfx("whoosh", 0.35);
    }

    // Sink animation for objects bots swallowed.
    for (let i = this.sinking.length - 1; i >= 0; i--) {
      const sink = this.sinking[i]!;
      sink.t += dt;
      // Free fall straight down through the rival's hole, no pull sideways.
      const def = sink.item.definition;
      const root = sink.item.root;
      const drop = 0.5 * 24 * sink.t * sink.t;
      root.position.x = sink.item.x;
      root.position.z = sink.item.z;
      if (root.userData.amp !== undefined) { root.userData.phase += dt * 14; root.userData.amp = 1.6; }
      root.position.y = def.height / 2 + 0.18 - drop;
      root.rotation.z += dt * 2.2;
      root.rotation.x += dt * 1.3;
      if (root.position.y + def.height < -SWALLOW_DEPTH) {
        root.visible = false;
        const bot = sink.bot;
        bot.score += def.score;
        bot.eaten++;
        bot.targetRadius = Math.min(MAX_RADIUS, bot.targetRadius + def.growth);
        this.botViews.get(bot.id)?.visual.bump(0.3 + def.tier * 0.08);
        sink.item.respawnAt = this.elapsed + this.respawnDelay(sink.item.definition);
        this.sinking.splice(i, 1);
      }
    }

    // Respawn eaten objects so the city never runs dry.
    for (const item of this.items) {
      if (item.consumed && item.respawnAt !== undefined && this.elapsed >= item.respawnAt) this.respawnItem(item);
    }

  }

  private respawnContestant(c: Contestant) {
    let best = { x: 0, z: 0, score: -1 };
    for (let tries = 0; tries < 12; tries++) {
      const x = this.random.range(-RAIL + 6, RAIL - 6);
      const z = this.random.range(-RAIL + 6, RAIL - 6);
      let nearest = Infinity;
      for (const other of [this.meContestant(), ...this.bots]) {
        if (other !== c && other.alive) nearest = Math.min(nearest, Math.hypot(other.x - x, other.z - z) - other.radius);
      }
      if (nearest > best.score) best = { x, z, score: nearest };
    }
    c.x = best.x;
    c.z = best.z;
    c.alive = true;
    c.respawnAt = 0;
    c.radius = Math.max(START_RADIUS, c.radius);
    if ("tx" in c) {
      (c as Bot).tx = c.x;
      (c as Bot).tz = c.z;
    }
  }

  private respawnItem(item: WorldItem) {
    const occupied = this.items
      .filter((other) => other !== item && !other.consumed)
      .map((other) => ({ x: other.x, z: other.z, radius: other.definition.footprint }));
    occupied.push({ x: this.hole.x, z: this.hole.z, radius: this.hole.radius + 2 });
    for (const bot of this.bots) {
      if (bot.alive) occupied.push({ x: bot.x, z: bot.z, radius: bot.radius + 2 });
    }

    const definition = item.home?.definition ?? item.definition;
    let found: { x: number; z: number; vx: number; vz: number } | undefined;
    if (item.home) {
      // Static props grow back on their own plot spot.
      if (clearOf(item.home, definition.footprint, occupied)) found = { x: item.home.x, z: item.home.z, vx: 0, vz: 0 };
    } else {
      found = this.findSpot(definition, occupied);
    }
    if (!found) {
      // Spot is busy (a hole sits on it) — try again shortly.
      item.respawnAt = this.elapsed + 1.5;
      return;
    }

    this.cityGroup.remove(item.root);
    this.blobAnchors.remove(item.shadow);
    const fresh = this.makeItem(this.nextItemId++, definition, found.x, found.z, found.vx, found.vz);
    Object.assign(item, fresh, { home: item.home, respawnAt: undefined, body: undefined });
  }

  /** Seconds until an eaten object grows back (big buildings take longer). */
  private respawnDelay(definition: ObjectDefinition) {
    return (definition.tier >= 5 ? 22 : definition.tier >= 3 ? 12 : 7) + this.random.next() * 6;
  }

  /**
   * Find a legal spawn spot for `definition`: a block interior for static
   * props, a lane for traffic/pedestrians, anywhere free for birds. Returns
   * undefined when 40 attempts found no gap that respects the minimum
   * distance to every occupied circle.
   */
  private findSpot(
    definition: ObjectDefinition,
    occupied: ReadonlyArray<{ x: number; z: number; radius: number }>,
  ): { x: number; z: number; vx: number; vz: number } | undefined {
    const radius = definition.footprint;
    for (let attempt = 0; attempt < 40; attempt++) {
      if (definition.motion === "road" || definition.motion === "walk") {
        const candidate = laneSpot(() => this.random.next(), definition.motion, definition.id, definition.speed);
        if (clearOf(candidate, radius, occupied)) {
          return { x: candidate.x, z: candidate.z, vx: candidate.vx, vz: candidate.vz };
        }
      } else if (definition.motion === "fly") {
        const candidate = {
          x: this.random.range(-RAIL + 4, RAIL - 4),
          z: this.random.range(-RAIL + 4, RAIL - 4),
        };
        if (clearOf(candidate, radius, occupied)) {
          const dir = this.random.next() < 0.5 ? 1 : -1;
          return { x: candidate.x, z: candidate.z, vx: definition.speed * dir, vz: 0 };
        }
      } else {
        const candidate = blockSpot(() => this.random.next(), radius);
        if (isInBlockInterior(candidate.x, candidate.z, radius) && clearOf(candidate, radius, occupied)) {
          return { x: candidate.x, z: candidate.z, vx: 0, vz: 0 };
        }
      }
    }
    return undefined;
  }


  /** Soft ground disc. Not added to the scene; BlobShadows draws the visible ones. */
  private attachBlob(definition: ObjectDefinition, x: number, z: number) {
    const shadow = new THREE.Object3D();
    shadow.position.set(x, 0.2, z);
    shadow.visible = definition.motion !== "fly" && definition.tier >= 3;
    shadow.userData.radius = definition.footprint * 0.9;
    this.blobAnchors.add(shadow);
    this.blobs.add(shadow);
    return shadow;
  }

  /**
   * Hide anything outside the camera frustum. A small radius margin keeps
   * buildings that sit just off-screen in the shadow map. Gameplay still
   * simulates hidden objects; only the draw is skipped.
   */
  private cullEntities() {
    // lookAt() updates the quaternion only; the frustum needs this frame's world matrix.
    this.camera.updateMatrixWorld();
    this._proj.multiplyMatrices(this.camera.projectionMatrix, this.camera.matrixWorldInverse);
    this._frustum.setFromProjectionMatrix(this._proj);
    for (const item of this.items) {
      if (item.consumed) {
        item.shadow.visible = false;
        // Bot swallows stay visible while they fall through the hole.
        const sinking = this.sinking.some((entry) => entry.item === item);
        if (!sinking) {
          item.root.visible = false;
          continue;
        }
      }
      const def = item.definition;
      const y = item.falling || item.consumed ? item.root.position.y : def.height * 0.5;
      this._sphere.center.set(item.x, y, item.z);
      this._sphere.radius = Math.max(def.footprint, def.height * 0.5, 1.2) + 6;
      const visible = this._frustum.intersectsSphere(this._sphere);
      item.root.visible = visible;
      item.shadow.visible = !item.consumed && visible && !item.falling && def.motion !== "fly" && def.tier >= 3;
    }
    const extent = shadowExtent(this.camera.position.y);
    if (this.sun) {
      const cam = this.sun.shadow.camera;
      cam.left = -extent;
      cam.right = extent;
      cam.top = extent;
      cam.bottom = -extent;
      cam.updateProjectionMatrix();
    }
    for (const bot of this.bots) {
      const view = this.botViews.get(bot.id);
      if (!view) continue;
      this._sphere.center.set(bot.x, 0.4, bot.z);
      this._sphere.radius = bot.radius + 4;
      const show = bot.alive && this._frustum.intersectsSphere(this._sphere);
      view.visual.setVisible(show);
      view.label.visible = show;
    }
    for (const visual of this.remoteVisuals.values()) {
      this._sphere.center.set(visual.x, 0.4, visual.z);
      this._sphere.radius = visual.currentRadius + 4;
      visual.setVisible(this._frustum.intersectsSphere(this._sphere));
    }
  }

  /** Shader and particle work for rival holes that survived the frustum test. */
  private presentBotViews(dt: number) {
    for (const bot of this.bots) {
      const view = this.botViews.get(bot.id);
      if (!view || !view.visual.group.visible) continue;
      view.visual.update(dt, bot.x, bot.z, bot.radius);
      view.label.position.set(bot.x, 3 + bot.radius * 0.6, bot.z - bot.radius * 0.4);
    }
  }

  private animate = () => {
    if (this.destroyed) return;
    const dt = Math.min(1 / 20, this.clock.getDelta());
    if (!this.finished) {
      this.elapsed += dt;
      try {
        this.updateInput(dt);
        this.updateHole(dt);
        this.physics?.step();
        this.updateItems(dt);
        if (this.bots.length) this.updateBots(dt);
        this.updateCamera(dt);
        this.cullEntities();
        for (const visual of this.remoteVisuals.values()) visual.followTarget(dt);
        if (this.bots.length) this.presentBotViews(dt);
        this.syncHoleUniforms();
      } catch {
        // Rapier/WASM can raise a transient trap on cold start or memory
        // growth; the next frame recovers. Never let it kill the loop.
        if (this.frame < 3) console.warn("[hole] physics frame skipped");
      }
      this.comboLife -= dt;
      if (this.comboLife <= 0) this.combo = 0;
      this.statsAccumulator += dt;
      if (this.statsAccumulator >= 0.08) {
        this.statsAccumulator = 0;
        this.options.onStats?.({
          score: this.score,
          eaten: this.eaten,
          radius: this.hole.radius,
          time: Math.max(0, this.options.duration - this.elapsed),
          combo: this.combo,
          ...this.standings(),
          respawnIn: this.alive ? undefined : Math.max(0, this.respawnAt - this.elapsed),
          feed: this.feed,
        });
      }
      if (this.elapsed >= this.options.duration) {
        this.finished = true;
        const standings = this.standings();
        this.options.onFinish?.({
          score: this.score,
          eaten: this.eaten,
          rank: standings.rank,
          total: this.bots.length + 1,
          kills: this.kills,
        });
      }
    }
    this.fx.update(dt);
    this.crowd.update(dt);
    this.props.update();
    this.blobs.update();
    this.updatePopups(dt);
    this.frame++;
    this.renderer.render(this.scene, this.camera);
  };
}
